import { useNavigate, useRouter, useSearch } from '@tanstack/react-router'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useRenderedDocChatStorageOwner } from 'features/knowledgeDocumentViewer/hooks/useRenderedDocChatStorageOwner'
import {
  DocumentHistoryItem,
  DocumentTab,
  type FragmentHighlight,
  type LotoPanelState,
  type RegionHighlight,
  SearchHistoryItem,
  type SearchStateUpdate,
  TabHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import { applyStoredComposerDraftsToOpenTabs } from 'features/knowledgeDocumentViewer/utils/agenticChatComposerDraft'
import {
  getPersistedTabState,
  persistTabState,
} from 'features/knowledgeDocumentViewer/utils/documentPageSessionStorage'
import {
  reconcileTabsWithDocumentsUrl,
  shouldSkipSelfWrittenDocumentUrlReconcile,
} from 'features/knowledgeDocumentViewer/utils/reconcileTabsWithDocumentsUrl'
import { serializeSelectedRegionsParam } from 'features/knowledgeDocumentViewer/utils/selectedRegionsParam'
import {
  applyDocumentUrlIntentToTabs,
  applySearchUrlPatchToTabs,
  CLEAR_ALL_VIEWER_PARAMS,
  CLEAR_DOCUMENT_PARAMS,
  createDefaultTab,
  createDocumentTab,
  createInitialTab,
  mergeSearchStateIntoTabs,
  searchParamsFromSearchState,
} from 'features/knowledgeDocumentViewer/utils/tabStateUrlHelpers'
import {
  chartLocalStateKey,
  clearChartLocalState,
} from 'features/timeSeries/utils/chartLocalState'
import { useOptionalWorkspaceTabs } from 'features/workspaceTabs/contexts/WorkspaceTabsContext'
import {
  workspaceScope,
  type WorkspaceScopeMode,
} from 'features/workspaceTabs/utils/workspaceScope'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CLEAR_SEARCH_VIEWER_PARAMS,
  DocumentsSearchParams,
  DocumentViewerUrlPaths,
  getSearchStatePatchFromUrl,
} from 'routes/documents/-searchParams'

export const useTabState = (
  basePath: DocumentViewerUrlPaths,
  siteId: string,
  options?: {
    /** Embedded viewers persist tabs under `:embedded` scope, not workspace scope. */
    embedded?: boolean
    /** Overrides the default `:embedded` suffix when `embedded` is true. */
    scopeSuffix?: string
    /**
     * When false, ignore sessionStorage tabs unless the URL already has a
     * document (Data Labeling samples a fresh page on session start).
     */
    restorePersisted?: boolean
  }
) => {
  const search = useSearch({ from: basePath })
  const { isPreviewMode, isPipelineConfigScopeLoading } = usePipelineConfig()
  const corpusMode: WorkspaceScopeMode = isPreviewMode ? 'preview' : 'active'
  const tabStateScope = `${workspaceScope(siteId, corpusMode)}${
    options?.scopeSuffix ?? (options?.embedded ? ':embedded' : '')
  }`
  // WorkspaceTabsProvider owns tab state when mounted; otherwise local + session persistence.
  const workspaceTabs = useOptionalWorkspaceTabs()
  const storageOwnerRef = useRenderedDocChatStorageOwner()

  const [initialState] = useState(() => {
    const persisted = getPersistedTabState(tabStateScope)
    const hasUrlDocument = !!search.documentID

    let result: { openTabs: DocumentTab[]; activeTabId: string }

    if (hasUrlDocument && persisted) {
      const activePersisted = persisted.openTabs.find(
        (t) => t.id === persisted.activeTabId
      )
      const activeItem = activePersisted?.history[activePersisted.currentIndex]
      if (
        activeItem?.type === 'document' &&
        activeItem.documentId === search.documentID
      ) {
        result = {
          openTabs: applyDocumentUrlIntentToTabs(
            persisted.openTabs,
            persisted.activeTabId,
            search
          ),
          activeTabId: persisted.activeTabId,
        }
      } else {
        const existingTab = persisted.openTabs.find((t) => {
          const item = t.history[t.currentIndex]
          return (
            item.type === 'document' && item.documentId === search.documentID
          )
        })
        if (existingTab) {
          result = {
            openTabs: applyDocumentUrlIntentToTabs(
              persisted.openTabs,
              existingTab.id,
              search
            ),
            activeTabId: existingTab.id,
          }
        } else {
          const newTab = createInitialTab(
            search.documentID,
            search.pageNumber,
            search.selectedBoxes,
            search.selectedRegions
          )
          result = {
            openTabs: [...persisted.openTabs, newTab],
            activeTabId: newTab.id,
          }
        }
      }
    } else if (hasUrlDocument) {
      const tab = createInitialTab(
        search.documentID,
        search.pageNumber,
        search.selectedBoxes,
        search.selectedRegions
      )
      result = { openTabs: [tab], activeTabId: tab.id }
    } else if (persisted && options?.restorePersisted !== false) {
      result = {
        openTabs: persisted.openTabs,
        activeTabId: persisted.activeTabId,
      }
    } else {
      const tab = createDefaultTab()
      result = { openTabs: [tab], activeTabId: tab.id }
    }

    if (!hasUrlDocument) {
      const urlPatch = getSearchStatePatchFromUrl(search)
      if (urlPatch && Object.keys(urlPatch).length > 0) {
        result = applySearchUrlPatchToTabs(
          result.openTabs,
          result.activeTabId,
          urlPatch
        )
      }
    }

    return {
      ...result,
      openTabs: applyStoredComposerDraftsToOpenTabs(result.openTabs),
    }
  })

  const [localOpenTabs, setLocalOpenTabs] = useState(initialState.openTabs)
  const [localActiveTabId, setLocalActiveTabId] = useState<string>(
    initialState.activeTabId
  )
  const openTabs = workspaceTabs?.openTabs ?? localOpenTabs
  const setOpenTabs = workspaceTabs?.setOpenTabs ?? setLocalOpenTabs
  const activeTabId = workspaceTabs?.activeTabId ?? localActiveTabId
  const setActiveTabId = workspaceTabs?.setActiveTabId ?? setLocalActiveTabId

  const openTabsRef = useRef(openTabs)
  openTabsRef.current = openTabs

  const activeTabIdRef = useRef(activeTabId)
  activeTabIdRef.current = activeTabId
  const lastSelfWrittenDocumentIdRef = useRef<string | undefined>(undefined)

  // Legacy-only persistence (skipped when workspace provider owns tabs).
  useEffect(() => {
    if (workspaceTabs) return
    const timer = setTimeout(() => {
      persistTabState(
        storageOwnerRef.current,
        tabStateScope,
        openTabs,
        activeTabId
      )
    }, 300)
    return () => clearTimeout(timer)
  }, [openTabs, activeTabId, tabStateScope, workspaceTabs, storageOwnerRef])

  // Legacy-only unmount / reload flush.
  useEffect(() => {
    if (workspaceTabs) return
    const persist = () => {
      persistTabState(
        storageOwnerRef.current,
        tabStateScope,
        openTabsRef.current,
        activeTabIdRef.current
      )
    }
    window.addEventListener('pagehide', persist)
    return () => {
      window.removeEventListener('pagehide', persist)
      persist()
    }
  }, [tabStateScope, workspaceTabs, storageOwnerRef])

  const activeTab = useMemo(() => {
    return openTabs.find((t) => t.id === activeTabId) ?? openTabs[0]
  }, [openTabs, activeTabId])

  const navigate = useNavigate({ from: basePath })

  const updateURLParams = useCallback(
    (updates: DocumentsSearchParams) => {
      navigate({
        search: (prev) => ({
          ...prev,
          ...updates,
        }),
        replace: true,
      })
    },
    [navigate]
  )

  const writeSearchStateToURL = useCallback(
    (ss: SearchHistoryItem['searchState']) => {
      updateURLParams({
        ...CLEAR_DOCUMENT_PARAMS,
        ...searchParamsFromSearchState(ss),
      })
    },
    [updateURLParams]
  )

  const writeHistoryItemToURL = useCallback(
    (item: TabHistoryItem) => {
      lastSelfWrittenDocumentIdRef.current =
        item.type === 'document' ? item.documentId : undefined
      if (item.type === 'document') {
        updateURLParams({
          ...CLEAR_SEARCH_VIEWER_PARAMS,
          documentID: item.documentId,
          pageNumber: item.pageNumber,
          selectedBoxes: item.selectedBoxIds,
          selectedRegions: serializeSelectedRegionsParam(
            item.regionHighlight?.boxes ?? []
          ),
        })
      } else if (item.type === 'search') {
        writeSearchStateToURL(item.searchState)
      } else {
        updateURLParams(CLEAR_ALL_VIEWER_PARAMS)
      }
    },
    [updateURLParams, writeSearchStateToURL]
  )

  const setWorkspaceScope = workspaceTabs?.setWorkspaceScope
  const previousTabStateScopeRef = useRef(tabStateScope)
  useEffect(() => {
    if (setWorkspaceScope) {
      // The workspace scope is site + corpus, never this viewer's `:embedded`
      // bucket — and publishing before the corpus resolves would swap the whole
      // tab set on the way from the default `active` to the site's preference.
      if (isPipelineConfigScopeLoading) return
      setWorkspaceScope(workspaceScope(siteId, corpusMode))
      return
    }
    if (previousTabStateScopeRef.current === tabStateScope) return
    previousTabStateScopeRef.current = tabStateScope

    const persisted = getPersistedTabState(tabStateScope)
    if (persisted) {
      const reconciled = reconcileTabsWithDocumentsUrl(
        persisted.openTabs,
        persisted.activeTabId,
        search
      )
      if (reconciled) {
        setOpenTabs(applyStoredComposerDraftsToOpenTabs(reconciled.openTabs))
        setActiveTabId(reconciled.activeTabId)
        return
      }
      setOpenTabs(persisted.openTabs)
      setActiveTabId(persisted.activeTabId)
      const activeTab =
        persisted.openTabs.find((tab) => tab.id === persisted.activeTabId) ??
        persisted.openTabs[0]
      if (activeTab) {
        writeHistoryItemToURL(activeTab.history[activeTab.currentIndex])
      }
      return
    }

    const urlReconciled = reconcileTabsWithDocumentsUrl(
      [createDefaultTab()],
      '',
      search
    )
    if (urlReconciled) {
      setOpenTabs(applyStoredComposerDraftsToOpenTabs(urlReconciled.openTabs))
      setActiveTabId(urlReconciled.activeTabId)
      return
    }

    const freshTab = createDefaultTab()
    setOpenTabs([freshTab])
    setActiveTabId(freshTab.id)
    writeHistoryItemToURL(freshTab.history[0])
  }, [
    corpusMode,
    isPipelineConfigScopeLoading,
    search,
    setActiveTabId,
    setOpenTabs,
    setWorkspaceScope,
    siteId,
    tabStateScope,
    writeHistoryItemToURL,
  ])

  const router = useRouter()
  const isHistoryTraversalRef = useRef(false)
  const syncUrlAfterTabRestoreRef = useRef(false)
  useEffect(
    () =>
      router.history.subscribe(({ action }) => {
        isHistoryTraversalRef.current =
          action.type === 'BACK' ||
          action.type === 'FORWARD' ||
          action.type === 'GO'
      }),
    [router.history]
  )

  const hasSyncedUrlRef = useRef(false)
  useEffect(() => {
    if (isPipelineConfigScopeLoading) return

    const urlDocumentId = search.documentID
    const activeTab = openTabsRef.current.find(
      (tab) => tab.id === activeTabIdRef.current
    )
    const activeItem = activeTab?.history[activeTab.currentIndex]
    const activeTabMatchesUrlDocument =
      urlDocumentId !== undefined &&
      activeItem?.type === 'document' &&
      activeItem.documentId === urlDocumentId
    if (
      shouldSkipSelfWrittenDocumentUrlReconcile({
        urlDocumentId,
        lastSelfWrittenDocumentId: lastSelfWrittenDocumentIdRef.current,
        activeTabMatchesUrlDocument,
      })
    ) {
      hasSyncedUrlRef.current = true
      return
    }

    const isSearchHistoryTraversal =
      isHistoryTraversalRef.current && urlDocumentId === undefined
    const reconciled = reconcileTabsWithDocumentsUrl(
      openTabsRef.current,
      activeTabIdRef.current,
      search,
      { isHistoryTraversal: isSearchHistoryTraversal }
    )
    if (reconciled) {
      setOpenTabs(applyStoredComposerDraftsToOpenTabs(reconciled.openTabs))
      setActiveTabId(reconciled.activeTabId)
      if (isSearchHistoryTraversal) syncUrlAfterTabRestoreRef.current = true
      return
    }

    if (isSearchHistoryTraversal) {
      const tab = openTabsRef.current.find(
        (t) => t.id === activeTabIdRef.current
      )
      if (tab) {
        writeHistoryItemToURL(tab.history[tab.currentIndex])
      }
      return
    }

    if (hasSyncedUrlRef.current) return
    hasSyncedUrlRef.current = true

    const tab = openTabsRef.current.find((t) => t.id === activeTabIdRef.current)
    if (tab) {
      writeHistoryItemToURL(tab.history[tab.currentIndex])
    }
  }, [
    isPipelineConfigScopeLoading,
    search,
    setActiveTabId,
    setOpenTabs,
    tabStateScope,
    writeHistoryItemToURL,
  ])

  const addTab = useCallback(
    (tab: DocumentTab) => {
      if (openTabsRef.current.some((t) => t.id === tab.id)) {
        setActiveTabId(tab.id)
        return
      }
      setOpenTabs((prevTabs) => {
        if (prevTabs.some((t) => t.id === tab.id)) {
          return prevTabs
        }
        return [...prevTabs, tab]
      })
      writeHistoryItemToURL(tab.history[tab.currentIndex])
      setActiveTabId(tab.id)
    },
    [setActiveTabId, setOpenTabs, writeHistoryItemToURL]
  )

  const addDocumentTab = useCallback(
    ({
      documentId,
      pageNumber = 1,
      selectedBoxIds = [],
      query = '',
      searchOriginQuery,
      fragmentHighlight,
      regionHighlight,
      selectedGraphNodeId,
      lotoPanel,
    }: {
      documentId: string
      pageNumber?: number
      selectedBoxIds?: string[]
      query?: string
      searchOriginQuery?: string
      fragmentHighlight?: FragmentHighlight
      regionHighlight?: RegionHighlight
      selectedGraphNodeId?: string
      lotoPanel?: LotoPanelState
    }) => {
      addTab(
        createDocumentTab(
          documentId,
          pageNumber,
          selectedBoxIds,
          query,
          fragmentHighlight,
          searchOriginQuery,
          regionHighlight,
          selectedGraphNodeId,
          lotoPanel
        )
      )
    },
    [addTab]
  )

  const removeTab = useCallback(
    (tabId: string): boolean => {
      if (workspaceTabs) return workspaceTabs.closeWorkspaceTab(tabId)

      // Clear orphaned chart localStorage keyed by this tab id.
      clearChartLocalState(chartLocalStateKey(siteId, tabId))

      if (openTabs.length === 1) {
        const freshTab = createDefaultTab()
        setOpenTabs([freshTab])
        setActiveTabId(freshTab.id)
        writeHistoryItemToURL(freshTab.history[0])
        return true
      }

      const newTabs = openTabs.filter((t) => t.id !== tabId)
      setOpenTabs(newTabs)

      if (activeTabId === tabId && newTabs.length > 0) {
        const newActiveTab = newTabs[newTabs.length - 1]
        setActiveTabId(newActiveTab.id)
        writeHistoryItemToURL(newActiveTab.history[newActiveTab.currentIndex])
      }
      return true
    },
    [
      openTabs,
      activeTabId,
      siteId,
      setActiveTabId,
      setOpenTabs,
      workspaceTabs,
      writeHistoryItemToURL,
    ]
  )

  const pushToHistory = useCallback(
    (tabId: string, item: TabHistoryItem) => {
      const nextTabs = openTabsRef.current.map((tab) => {
        if (tab.id !== tabId) return tab
        const newHistory = [...tab.history.slice(0, tab.currentIndex + 1), item]
        return {
          ...tab,
          history: newHistory,
          currentIndex: newHistory.length - 1,
        }
      })
      openTabsRef.current = nextTabs
      setOpenTabs(nextTabs)
      if (tabId === activeTabId) {
        writeHistoryItemToURL(item)
      }
    },
    [setOpenTabs, activeTabId, writeHistoryItemToURL]
  )

  const navigateHistory = useCallback(
    (tabId: string, direction: 'back' | 'forward') => {
      const tab = openTabs.find((t) => t.id === tabId)
      if (!tab) return

      const newIndex =
        direction === 'back' ? tab.currentIndex - 1 : tab.currentIndex + 1

      if (newIndex < 0 || newIndex >= tab.history.length) return

      const targetItem = tab.history[newIndex]

      setOpenTabs((prevTabs) =>
        prevTabs.map((t) =>
          t.id === tabId ? { ...t, currentIndex: newIndex } : t
        )
      )

      if (tabId === activeTabId) {
        writeHistoryItemToURL(targetItem)
      }
    },
    [openTabs, activeTabId, setOpenTabs, writeHistoryItemToURL]
  )

  const goBack = useCallback(
    (tabId: string) => navigateHistory(tabId, 'back'),
    [navigateHistory]
  )

  const goForward = useCallback(
    (tabId: string) => navigateHistory(tabId, 'forward'),
    [navigateHistory]
  )

  const updateDocumentHistoryItem = useCallback(
    (tabId: string, updates: Partial<Omit<DocumentHistoryItem, 'type'>>) => {
      setOpenTabs((prevTabs) => {
        return prevTabs.map((tab) => {
          if (tab.id !== tabId) return tab
          const currentItem = tab.history[tab.currentIndex]
          if (currentItem.type !== 'document') return tab
          const updatedItem: DocumentHistoryItem = {
            ...currentItem,
            ...updates,
          }
          const newHistory = [...tab.history]
          newHistory[tab.currentIndex] = updatedItem
          return { ...tab, history: newHistory }
        })
      })

      if (tabId === activeTabId) {
        if (updates.documentId !== undefined) {
          lastSelfWrittenDocumentIdRef.current = updates.documentId
        }
        updateURLParams({
          ...CLEAR_SEARCH_VIEWER_PARAMS,
          ...(updates.documentId !== undefined && {
            documentID: updates.documentId,
          }),
          ...(updates.pageNumber !== undefined && {
            pageNumber: updates.pageNumber,
          }),
          ...(updates.selectedBoxIds !== undefined && {
            selectedBoxes: updates.selectedBoxIds,
          }),
          // `in` (not !== undefined): clearing the highlight passes
          // regionHighlight: undefined explicitly, which must clear the param.
          ...('regionHighlight' in updates && {
            selectedRegions: serializeSelectedRegionsParam(
              updates.regionHighlight?.boxes ?? []
            ),
          }),
        })
      }
    },
    [setOpenTabs, activeTabId, updateURLParams]
  )

  const updateSearchHistoryItem = useCallback(
    (tabId: string, searchStateUpdates: SearchStateUpdate) => {
      setOpenTabs((prevTabs) => {
        const { nextTabs, urlSync } = mergeSearchStateIntoTabs(
          prevTabs,
          tabId,
          activeTabId,
          searchStateUpdates
        )
        if (urlSync) {
          writeSearchStateToURL(urlSync)
        }
        return nextTabs
      })
    },
    [activeTabId, setOpenTabs, writeSearchStateToURL]
  )

  const renameTab = useCallback(
    (tabId: string, label: string) => {
      setOpenTabs((prevTabs) =>
        prevTabs.map((tab) =>
          tab.id === tabId ? { ...tab, customLabel: label || undefined } : tab
        )
      )
    },
    [setOpenTabs]
  )

  const reorderTabs = useCallback(
    (newTabs: DocumentTab[]) => {
      setOpenTabs(newTabs)
    },
    [setOpenTabs]
  )

  const canGoBack = useMemo(() => {
    return activeTab.currentIndex > 0
  }, [activeTab])

  const canGoForward = useMemo(() => {
    return activeTab.currentIndex < activeTab.history.length - 1
  }, [activeTab])

  const activeHistoryItem = activeTab.history[activeTab.currentIndex]

  useEffect(() => {
    if (!syncUrlAfterTabRestoreRef.current) return
    syncUrlAfterTabRestoreRef.current = false
    writeHistoryItemToURL(activeHistoryItem)
  }, [activeHistoryItem, writeHistoryItemToURL])

  const switchToTab = useCallback(
    (tabId: string) => {
      const tab = openTabs.find((t) => t.id === tabId)
      if (tab) {
        setActiveTabId(tabId)
        writeHistoryItemToURL(tab.history[tab.currentIndex])
      }
    },
    [openTabs, setActiveTabId, writeHistoryItemToURL]
  )

  return {
    openTabs,
    activeTabId,
    setActiveTabId: switchToTab,
    addTab,
    addDocumentTab,
    removeTab,
    renameTab,
    reorderTabs,
    activeHistoryItem,
    pushToHistory,
    goBack,
    goForward,
    updateDocumentHistoryItem,
    updateSearchHistoryItem,
    canGoBack,
    canGoForward,
  }
}
